import { memo, useCallback, useMemo } from "react";
import { IGif, IUser } from "@/common/services/types";
import { UserInfo } from "../UserInfo";
import { clsx } from "clsx";
import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { EventSource, metricsBus } from "@/common/buses";
import { useUserSubscription } from "@/common/hooks/useUserSubscription";
import "./MetaInfo.scss";
import { updateCtaLink } from "@/common/utils/boostedGifUtils";
import { Button } from "@/components/buttons/Button";
import { GifNiches } from "../GifNiches";

interface IMetaInfoProps {
  gif: IGif;
  user: IUser | undefined;
  currentUser: CurrentUserObject | null;
  isVisible: boolean;
  position: number;
  playCount: number;
  isLite: boolean;
}

export const MetaInfo = memo(({
  gif,
  user,
  currentUser,
  playCount,
  position,
  isLite
}: IMetaInfoProps) => {
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const onProfileMetric = useCallback((value: string) => {
    metricsBus.emit("profileClicked", {
      gifId: gif.id,
      creator: value,
      source: eventSource?.name ?? EventSource.Other,
      sourceId: eventSource?.id ?? null,
      position,
      playCount,
      userName: currentUser?.username,
    });

    if (gif.cta?.campaignId) {
      metricsBus.emit("boostProfileClicked", {
        campaignId: gif.cta.campaignId
      });
    }
  }, [eventSource, position, playCount, gif]);

  const hasCta = useMemo(() => gif.cta && gif.cta.text && gif.cta.link, [gif.cta]);
  const ctaUrl = useMemo(() => updateCtaLink(gif.cta?.link ?? null, position, gif.cta?.showReason ?? null), [gif.cta]);

  const { subscribed, subscribe, unsubscribe } = useUserSubscription(gif.userName, true);

  const onSubscribe = useCallback(() => {
    subscribed ? unsubscribe() : subscribe({ gifId: gif.id, position, playCount });
  }, [gif, position, playCount, subscribed]);

  return (
    <div className={clsx("metaInfo", hasCta && "metaInfo_isBoosted")}>
      {user && <UserInfo
        isFollowing={subscribed}
        onFollow={onSubscribe}
        currentUser={currentUser}
        gif={gif}
        user={user}
        isLite={isLite}
        onProfileMetric={onProfileMetric}
      />}
      {!!gif.niches.length && !isLite && !hasCta && <GifNiches niches={gif.niches} />}
      {hasCta && ctaUrl && (
        <div className="boostedBtn">
          <Button
            variant="primary"
            size="s"
            aria-label={gif.cta?.text}
            fullWidth
            onClick={() => window.open(ctaUrl, "_blank", "noopener,noreferrer")}
          >{gif.cta?.text}</Button>
        </div>
      )}
    </div>
  );
});
